import type {Ranges} from '../types';

export type ScanForUnloadedRangesParams = {
	itemCount: number;
	minimumBatchSize: number;
	startIndex: number;
	stopIndex: number;
	isItemLoaded?: (index: number) => boolean;
};

export const scanForUnloadedRanges = ({
	isItemLoaded,
	itemCount,
	minimumBatchSize,
	startIndex,
	stopIndex,
}: ScanForUnloadedRangesParams): Ranges => {
	const unloadedRanges: Ranges = [];

	let rangeStartIndex: number | null = null;
	let rangeStopIndex: number | null = null;

	for (let index = startIndex; index <= stopIndex; index++) {
		const loaded = isItemLoaded?.(index);

		if (!loaded) {
			rangeStopIndex = index;
			if (rangeStartIndex === null) rangeStartIndex = index;
		} else if (rangeStopIndex !== null) {
			unloadedRanges.push(rangeStartIndex!, rangeStopIndex);
			rangeStartIndex = rangeStopIndex = null;
		}
	}

	// If :rangeStopIndex is not null it means we haven't ran out of unloaded rows.
	// Scan forward to try filling our :minimumBatchSize.
	if (rangeStopIndex !== null) {
		const potentialStopIndex = Math.min(
			Math.max(rangeStopIndex, rangeStartIndex! + minimumBatchSize - 1),
			itemCount - 1,
		);

		for (let index = rangeStopIndex + 1; index <= potentialStopIndex; index++) {
			if (!isItemLoaded?.(index)) {
				rangeStopIndex = index;
			} else {
				break;
			}
		}

		unloadedRanges.push(rangeStartIndex!, rangeStopIndex);
	}

	// Check to see if our first range ended prematurely.
	// In this case we should scan backwards to try filling our :minimumBatchSize.
	if (unloadedRanges.length) {
		while (
			unloadedRanges[1] - unloadedRanges[0] + 1 < minimumBatchSize &&
			unloadedRanges[0] > 0
		) {
			const index = unloadedRanges[0] - 1;
			if (isItemLoaded?.(index)) break;
			unloadedRanges[0] = index;
		}
	}

	return unloadedRanges;
};
